import React from 'react';
import {
  dashboardMuted,
  dashboardPage,
  dashboardStatCard,
  dashboardStatLabel,
  utcDataDisclaimer,
} from '../../../theme/dashboardLayout';
import { WellnessTrendChart } from '../charts/PopulationPulseCharts';
import ForecastMomentumPanel from '../forecasts/ForecastMomentumPanel';
import { earliestComparisonScore } from '../healthScoreContext';
import PulseHero from '../hero/PulseHero';
import SignalParticipationSection from '../participation/SignalParticipationSection';
import { AiForecastItemDto, AiMomentumDashboardResponse } from '../types';

interface Props {
  momentum: AiMomentumDashboardResponse;
  participation: AiMomentumDashboardResponse['signal_participation'];
  forecasts: AiForecastItemDto[];
  disclaimer: string;
  enrolledMembers?: number;
}

function ReadyDashboard({
  momentum,
  participation,
  forecasts,
  disclaimer,
  enrolledMembers,
}: Props): React.ReactElement {
  const daily = (momentum.daily ?? []).map((point) => ({
    date: point.date,
    wellness: point.wellness ?? null,
  }));
  const previousScore = earliestComparisonScore(daily);
  const contributorCount = momentum.scored_member_count ?? momentum.active_member_count;
  return (
    <div className={dashboardPage}>
      <PulseHero
        activeMembers={momentum.active_member_count}
        cohortMembers={momentum.cohort_member_count}
        enrolledMembers={enrolledMembers}
        anchorDate={momentum.anchor_date}
        score={momentum.population_momentum_score}
        direction={momentum.direction}
        wearablesPercent={participation.wearables_percent}
        facePercent={participation.face_percent}
        voicePercent={participation.voice_percent}
        windowDays={participation.reporting_window_days}
      />

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)] lg:items-stretch">
        <article className={`${dashboardStatCard} lg:col-start-1 lg:row-start-1`}>
          <h3 className={dashboardStatLabel}>Organisation Health Score during last 4 weeks</h3>
          <WellnessTrendChart points={daily} />
        </article>

        <ForecastMomentumPanel
          momentum={momentum}
          forecasts={forecasts}
          disclaimer={disclaimer}
          contributorCount={contributorCount}
          enrolledMembers={enrolledMembers}
          previousScore={previousScore}
        />
      </section>

      <SignalParticipationSection momentum={momentum} participation={participation} />
      <p className={`mt-8 ${dashboardMuted}`}>{utcDataDisclaimer}</p>
    </div>
  );
}

export default ReadyDashboard;
